import PropTypes from "prop-types"
import React, { useContext } from "react"
import { Helmet } from "react-helmet"

import Card from "../../../layout/card"
import { BodyText, InlineText, Title } from "../../../Typography/Typography"
import SingleGeoContext from "../../../../contexts/single_geo"
import CareTypeContext from "../../../../contexts/care_type"
import { POSTAL_TOWN_SCALE } from "../../../../utils/geographical_scales"

import makeHomeCareQuestions from "./data/home-care"
import makeCareHomeTownQuestions from "./data/care-home-towns"
import makeCareHomeNonTownQuestions from "./data/care-home-non-town"
import makeDementiaCareHomeQuestions from "./data/dementia-care-home"
import makeAgnosticQuestions from "./data/agnostic"
import { branchRecordType } from "../../../../shared-prop-types"

import { faq, list, listItem } from "./styles.module.scss"

const getQuestionBuilder = (townOrNonTown, pageType) =>
  ({
    town: {
      care_home: makeCareHomeTownQuestions,
      home_care: makeHomeCareQuestions,
      dementia: makeDementiaCareHomeQuestions,
      null: makeAgnosticQuestions,
    },
    non_town: {
      care_home: makeCareHomeNonTownQuestions,
      home_care: makeHomeCareQuestions,
      dementia: makeDementiaCareHomeQuestions,
      null: makeAgnosticQuestions,
    },
  }[townOrNonTown][pageType])

const FAQ = ({ careServiceType, branchRecords }) => {
  const {
    locationName,
    totalHomeCareCount,
    totalCareHomeCount,
    regulatoryBody,
    geographicScale,
    nursingCareHomeCount,
    dementiaCareHomeCount,
    ciCareHomeRatedPositivelyCount,
    ciHomeCareRatedPositivelyCount,
    cqcCareHomeRatedPositivelyCount,
    cqcHomeCareRatedPositivelyCount,
  } = useContext(SingleGeoContext)

  const { careType } = useContext(CareTypeContext)

  const pageType = careServiceType || careType

  const geographicLevel =
    geographicScale === POSTAL_TOWN_SCALE ? "town" : "non_town"

  const positivelyRatedCount = {
    ci: {
      care_home: ciCareHomeRatedPositivelyCount,
      home_care: ciHomeCareRatedPositivelyCount,
    },
    cqc: {
      care_home: cqcCareHomeRatedPositivelyCount,
      home_care: cqcHomeCareRatedPositivelyCount,
    },
  }[regulatoryBody]?.[careType]

  const questionBuilder = getQuestionBuilder(geographicLevel, pageType)
  const questions = questionBuilder({
    locationName,
    totalCareHomeCount,
    totalHomeCareCount,
    careType,
    regulatoryBody,
    nursingCareHomeCount,
    dementiaCareHomeCount,
    positivelyRatedCount,
    branchRecords,
    geographicScale,
  })

  return (
    <Card extraClasses={faq}>
      <Title>Frequently asked questions</Title>
      <ul className={list}>
        {questions
          .filter((question) => question)
          .map((question) => (
            <li key={`faq-item-${question?.question}`} className={listItem}>
              <InlineText semibold>{question?.question}</InlineText>
              <BodyText marginBottom>{question?.answer}</BodyText>
            </li>
          ))}
      </ul>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: questions
              .filter((question) => question)
              .map((question) => ({
                "@type": "Question",
                name: question?.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: question?.structuredDataAnswer || question?.answer,
                },
              })),
          })}
        </script>
      </Helmet>
    </Card>
  )
}

FAQ.propTypes = {
  careServiceType: PropTypes.oneOf(["dementia"]),
  branchRecords: PropTypes.arrayOf(branchRecordType),
}

FAQ.defaultProps = {
  careServiceType: undefined,
  branchRecords: [],
}

export default FAQ
